<template>
  <div class="home-top pa-4">
    <div class="home-top__intro" :class="{ 'home-top__intro--review': !isHome }">
      <div v-if="isHome">
        <div class="home-top__hello">
          {{ $t("hello") }} {{ $store.state.fullname }} {{ $t("dear") }}
        </div>
        <div v-if="canSeeOverview" class="home-top__sub">
          {{ $t("businessOverview") }} ·
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="home-top__period-link"
                v-bind="attrs"
                v-on="on"
              >
                {{ periodLabel }}
                <v-icon x-small class="home-top__period-link-icon"
                  >mdi-chevron-down</v-icon
                >
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="item in periods"
                :key="item.value"
                @click="setPeriod(item.value)"
              >
                <v-list-item-title>
                  <v-icon
                    v-if="period === item.value"
                    x-small
                    color="primary"
                    class="ms-1"
                  >
                    mdi-check
                  </v-icon>
                  {{ $t(item.label) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          {{ $t("inFinancialYear") }}
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="home-top__period-link"
                v-bind="attrs"
                v-on="on"
              >
                {{ dashboardYearName }}
                <v-icon x-small class="home-top__period-link-icon"
                  >mdi-chevron-down</v-icon
                >
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="year in yearItems"
                :key="year.id"
                @click="selectYear(year)"
              >
                <v-list-item-title>
                  <v-icon
                    v-if="year.name == dashboardYearName"
                    x-small
                    color="primary"
                    class="ms-1"
                  >
                    mdi-check
                  </v-icon>
                  {{ year.name }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <!-- <div v-if="isHome" class="home-top__review-wrap">
        <div class="home-top__greet" aria-live="polite">
          <transition name="home-top-greet-fade" mode="out-in">
            <span :key="'greet-' + greetStep" class="home-top__greet-inner">
              <v-icon
                size="18"
                class="home-top__greet-icon"
                :class="{ 'home-top__greet-icon--sun': greetStep === 1 }"
              >{{ greetIcon }}</v-icon>
              <span class="home-top__greet-text">{{ greetText }}</span>
            </span>
          </transition>
        </div>
      </div> -->
    </div>

    <template v-if="isHome">
      <div class="home-top__toolbar">
        <!-- desktop: chip row -->
        <div class="home-top__actions home-top__actions--desktop">
          <span class="home-top__actions-label">{{ $t("fast-access") }}</span>
          <v-tooltip
            v-if="canSeeCustomerReview"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="home-top__chip home-top__chip--customers"
                :class="{ 'home-top__chip--customers-active': value === 'customerReview' }"
                v-bind="attrs"
                v-on="on"
                @click="toggleReview"
              >
                <v-icon size="14" class="home-top__chip-pin">mdi-pin</v-icon>
                <span>{{ $t("customerReview") }}</span>
                <span class="home-top__chip-sep" aria-hidden="true">·</span>
                <span class="home-top__chip-count">{{
                  newCustomersCount != null ? newCustomersCount : "—"
                }}</span>
                <span class="home-top__chip-unit">{{ $t("per-person") }}</span>
              </button>
            </template>
            <span>{{ $t("fastAccessDefaultPinned") }}</span>
          </v-tooltip>
          <div
            v-for="item in actions"
            :key="item.id || item.to"
            class="home-top__chip-wrap"
          >
            <button
              type="button"
              class="home-top__chip"
              :class="{ 'home-top__chip--main': isActiveChip(item) }"
              @click="go(item.to, true)"
            >
              {{ item.title }}
            </button>
            <button
              type="button"
              class="home-top__chip-unpin"
              :disabled="!!pinningTo"
              @click.stop="unpin(item)"
            >
              <v-icon size="12">mdi-close</v-icon>
            </button>
          </div>
          <button
            type="button"
            class="home-top__chip home-top__chip--more"
            @click="pinDialog = true"
          >
            + {{ $t("more") }}
          </button>
        </div>

        <!-- mobile: half-width fast-access + customers mini -->
        <div class="home-top__actions-mobile">
          <button
            type="button"
            class="home-top__fast-btn"
            @click="fastAccessSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-flash-outline</v-icon>
            <span class="home-top__fast-btn-text">{{ $t("fast-access") }}</span>
            <v-icon size="18" color="#5B7C99">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="canSeeCustomerReview"
            type="button"
            class="home-top__customers-mini"
            :class="{
              'home-top__customers-mini--active': value === 'customerReview',
            }"
            :aria-label="$t('customerReview')"
            @click="toggleReview"
          >
            <v-icon size="20" color="#5B7C99">mdi-account-plus-outline</v-icon>
            <span
              v-if="newCustomersCount != null && Number(newCustomersCount) > 0"
              class="home-top__customers-mini-badge"
            >
              {{ newCustomersCount }} {{ $t("per-person") }}
            </span>
          </button>
        </div>
      </div>

      <v-bottom-sheet v-model="fastAccessSheet" scrollable>
        <v-card class="home-top__fast-sheet">
          <div class="home-top__fast-sheet-handle" />
          <div class="home-top__fast-sheet-header" dir="rtl">
            <span class="home-top__fast-sheet-spacer" />
            <div class="home-top__fast-sheet-title">{{ $t("fast-access") }}</div>
            <button
              type="button"
              class="home-top__fast-sheet-close"
              :aria-label="$t('back')"
              @click="fastAccessSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
          <div class="home-top__fast-sheet-body" dir="rtl">
            <button
              type="button"
              class="home-top__fast-sheet-more"
              @click="fromFastSheetMore"
            >
              + {{ $t("addShortcut") }}
            </button>
            <button
              v-for="item in actions"
              :key="'fast-' + (item.id || item.to)"
              type="button"
              class="home-top__fast-sheet-item"
              :class="{ 'home-top__fast-sheet-item--active': isActiveChip(item) }"
              @click="fromFastSheetGo(item)"
            >
              <span class="home-top__fast-sheet-item-title">{{ item.title }}</span>
              <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
          </div>
        </v-card>
      </v-bottom-sheet>

      <v-dialog
        v-model="pinDialog"
        :fullscreen="isMobile"
        content-class="home-top__pin-overlay"
        max-width="560"
        scrollable
      >
        <v-card class="home-top__pin-dialog">
          <div class="home-top__pin-top">
            <div class="home-top__pin-head">
              <span>{{ $t("fast-access") }}</span>
              <button type="button" class="home-top__pin-close" @click="pinDialog = false">
                <v-icon size="20">mdi-close</v-icon>
              </button>
            </div>
            <div class="home-top__pin-search">
              <v-text-field
                v-model="pinSearch"
                dense
                outlined
                hide-details
                :placeholder="$t('search-in-menu')"
                prepend-inner-icon="mdi-magnify"
              />
            </div>
          </div>
          <!-- <div
            class="home-top__pin-progress"
            :class="{ 'is-on': pinningTo }"
          >
            <span />
          </div> -->
          <v-card-text class="home-top__pin-body">
            <div v-if="!pinMenuGroups.length" class="home-top__pin-empty">
              {{ $t("no-data") }}
            </div>
            <div
              v-for="group in pinMenuGroups"
              :key="group.key"
              class="home-top__pin-group"
              :class="{ 'is-open': isPinGroupOpen(group) }"
            >
              <div class="home-top__pin-drawer">
                <button
                  type="button"
                  class="home-top__pin-drawer-toggle"
                  @click="togglePinGroup(group)"
                >
                  <span class="home-top__pin-parent-label">
                    <v-icon v-if="group.icon" size="18">
                      {{ group.icon }}
                    </v-icon>
                    <span>{{ group.parent ? group.parent.label : group.title }}</span>
                  </span>
                </button>
                <div class="home-top__pin-drawer-end">
                  <button
                    v-if="group.items.length"
                    type="button"
                    class="home-top__pin-chevron"
                    @click.stop="togglePinGroup(group)"
                  >
                    <v-icon size="20">
                      {{
                        isPinGroupOpen(group)
                          ? "mdi-chevron-up"
                          : "mdi-chevron-down"
                      }}
                    </v-icon>
                  </button>
                </div>
              </div>
              <div
                v-if="group.items.length && isPinGroupOpen(group)"
                class="home-top__pin-children"
              >
                <button
                  v-for="item in group.items"
                  :key="item.to"
                  type="button"
                  class="home-top__pin-item home-top__pin-item--child"
                  :class="{
                    'is-pinned': isPinned(item.to),
                    'is-saving': isSaving(item.to),
                  }"
                  @click="togglePin(item)"
                >
                  <span class="home-top__pin-child-label">{{ item.label }}</span>
                  <span class="home-top__pin-child-action">
                    <v-progress-circular
                      v-if="isSaving(item.to)"
                      indeterminate
                      size="14"
                      width="2"
                      color="#0066FF"
                    />
                    <v-icon v-else size="16">
                      {{ isPinned(item.to) ? "mdi-pin" : "mdi-pin-outline" }}
                    </v-icon>
                  </span>
                </button>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <div
        class="home-top__metrics"
        :class="{ 'home-top__metrics--limited': !canSeeOverview }"
      >
        <div v-if="canSeeOverview" class="home-top__main">
          <div class="home-top__kpis">
            <div class="home-top__kpi">
              <div class="home-top__kpi-head">
                <span class="home-top__kpi-icon home-top__kpi-icon--sell">
                  <v-icon size="16">mdi-cart-outline</v-icon>
                </span>
                <div class="home-top__kpi-label">{{ salesKpiLabel }}</div>
              </div>
              <div
                class="home-top__kpi-value"
                :class="{ 'is-long': isLongKpi(sumOfSells) }"
              >
                <span>{{ $numberFormat(sumOfSells) }}</span>
                <span>{{ $store.state.currency.name }}</span>
              </div>
              <div
                v-if="sparkPoints || sellChange != null"
                class="home-top__spark-row"
              >
                <svg v-if="sparkPoints" class="home-top__spark" viewBox="0 0 80 24">
                  <polyline
                    fill="none"
                    stroke="#0066FF"
                    stroke-width="2"
                    :points="sparkPoints"
                  />
                </svg>
                <div
                  v-if="sellChange != null"
                  class="home-top__kpi-foot home-top__kpi-foot--green"
                >
                  {{ sellChange }}٪
                </div>
              </div>
            </div>

            <div class="home-top__kpi">
              <div class="home-top__kpi-head">
                <span class="home-top__kpi-icon home-top__kpi-icon--profit">
                  <v-icon size="16">mdi-trophy-outline</v-icon>
                </span>
                <div class="home-top__kpi-label">{{ profitKpiLabel }}</div>
              </div>
              <div
                class="home-top__kpi-value"
                :class="{ 'is-long': isLongKpi(totalProfit) }"
              >
                <span>{{ $numberFormat(totalProfit) }}</span>
                <span>{{ $store.state.currency.name }}</span>
              </div>
            </div>

            <div class="home-top__kpi" @click="go('debtorList')">
              <div class="home-top__kpi-head">
                <span class="home-top__kpi-icon home-top__kpi-icon--debt">
                  <v-icon size="16">mdi-wallet-outline</v-icon>
                </span>
                <div class="home-top__kpi-label">{{ $t("receivables") }}</div>
              </div>
              <div
                class="home-top__kpi-value"
                :class="{ 'is-long': isLongKpi(debtorsTotal) }"
              >
                <span>{{ $numberFormat(debtorsTotal) }}</span>
                <span>{{ $store.state.currency.name }}</span>
              </div>
              <div class="home-top__kpi-foot home-top__kpi-foot--blue">
                {{ debtorsCount }} {{ $t("debtor") }}
              </div>
            </div>

            <div class="home-top__kpi" @click="go('creditorList')">
              <div class="home-top__kpi-head">
                <span class="home-top__kpi-icon home-top__kpi-icon--credit">
                  <v-icon size="16">mdi-swap-horizontal</v-icon>
                </span>
                <div class="home-top__kpi-label">{{ $t("commitments") }}</div>
              </div>
              <div
                class="home-top__kpi-value"
                :class="{ 'is-long': isLongKpi(creditorsTotal) }"
              >
                <span>{{ $numberFormat(creditorsTotal) }}</span>
                <span>{{ $store.state.currency.name }}</span>
              </div>
              <div class="home-top__kpi-foot home-top__kpi-foot--blue">
                {{ creditorsCount }} {{ $t("creditor") }}
              </div>
            </div>

            <div class="home-top__kpi" @click="go('ProductList')">
              <div class="home-top__kpi-head">
                <span class="home-top__kpi-icon home-top__kpi-icon--stock">
                  <v-icon size="16">mdi-package-variant-closed</v-icon>
                </span>
                <div class="home-top__kpi-label">{{ $t("belowOrderPoint") }}</div>
              </div>
              <div class="home-top__kpi-value">
                <span>{{ outOfStock }}</span>
                <span>{{ $t("itemUnit") }}</span>
              </div>
              <div class="home-top__kpi-foot home-top__kpi-foot--red">
                {{ $t("needSupply") }}
              </div>
            </div>
          </div>
          <button type="button" class="home-top__ai" @click="go('aiDashboard')">
            <span class="home-top__ai-icon">
              <v-icon x-small color="#0066FF">mdi-star-four-points</v-icon>
            </span>
            <span class="home-top__ai-text d-flex">
              <v-card-text class="text-body-1 pa-0 ma-0 home-top__ai-lead">{{
                $t("aiDashboardHintLead")
              }}</v-card-text>
              <v-card-text class="text-body-2 pa-0 ma-0 pt-1 ps-1">{{
                $t("aiDashboardHintRest")
              }}</v-card-text>
            </span>
            <span class="home-top__ai-action">
              {{ $t("activation") }}
              <v-icon x-small>mdi-arrow-left</v-icon>
            </span>
          </button>
          <div class="home-top__insights-main home-top__insights-primary">
            <div class="home-top__chart-col">
              <home-sales-trend :items="sellList" :date-type="dateType" />
              <checks-overview />
            </div>
            <div class="home-top__debtor-col">
              <home-debtors-card />
              <customer-attraction />
            </div>
          </div>
          <div
            class="home-top__lower-insights"
            :class="{
              'home-top__lower-insights--compact': !isDesktopHomeLayout,
            }"
          >
            <home-financial-groups class="home-top__cell-fg" />
            <home-debtor-creditor
              v-if="isDesktopHomeLayout"
              class="home-top__cell-dc"
            />
            <home-cost-payment class="home-top__cell-cost" :date-type="dateType" />
            <home-low-stock
              v-if="isDesktopHomeLayout"
              class="home-top__cell-low"
            />
          </div>
        </div>
        <div class="home-top__side">
          <market-day-price />
          <template v-if="canSeeOverview">
            <home-sales-channels :date-type="dateType" />
            <home-product-status :popular="popularProducts" :stagnant="stagnantProducts" />
            <home-sales-by-customer :date-type="dateType" />
            <home-bank-cash />
            <home-low-stock
              v-if="!isDesktopHomeLayout"
              class="home-top__side-low"
            />
          </template>
        </div>
      </div>
      <template v-if="canSeeOverview">
        <div class="home-top__split-row">
          <home-receipt-payment :date-type="dateType" :period-label="periodLabel" />
          <home-product-sales :date-type="dateType" />
        </div>
        <div class="home-top__split-bottom">
          <home-best-selling-products
            class="home-top__split-bsp"
            :date-type="dateType"
          />
          <home-customer-distribution
            class="home-top__split-cd"
            :date-type="dateType"
          />
          <home-debtor-creditor
            v-if="!isDesktopHomeLayout"
            class="home-top__split-dc"
          />
          <home-sales-receipts
            class="home-top__split-sr"
            :date-type="dateType"
            :period-label="periodLabel"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import MarketDayPrice from "./MarketDayPrice.vue";
import HomeSalesTrend from "./HomeSalesTrend.vue";
import HomeDebtorsCard from "./HomeDebtorsCard.vue";
import HomeSalesChannels from "./HomeSalesChannels.vue";
import HomeProductStatus from "./HomeProductStatus.vue";
import ChecksOverview from "./ChecksOverview.vue";
import HomeDebtorCreditor from "./HomeDebtorCreditor.vue";
import HomeFinancialGroups from "./HomeFinancialGroups.vue";
import HomeSalesByCustomer from "./HomeSalesByCustomer.vue";
import HomeSalesReceipts from "./HomeSalesReceipts.vue";
import CustomerAttraction from "./CustomerAttraction.vue";
import HomeCostPayment from "./HomeCostPayment.vue";
import HomeLowStock from "./HomeLowStock.vue";
import HomeReceiptPayment from "./HomeReceiptPayment.vue";
import HomeProductSales from "./HomeProductSales.vue";
import HomeCustomerDistribution from "./HomeCustomerDistribution.vue";
import HomeBestSellingProducts from "./HomeBestSellingProducts.vue";
import HomeBankCash from "./HomeBankCash.vue";
import moment from "moment-jalaali";

moment.loadPersian({ dialect: "persian-modern", usePersianDigits: false });

export default {
  name: "HomeDashboardTop",
  components: {
    MarketDayPrice,
    HomeSalesTrend,
    HomeDebtorsCard,
    HomeSalesChannels,
    HomeProductStatus,
    ChecksOverview,
    HomeDebtorCreditor,
    HomeFinancialGroups,
    HomeSalesByCustomer,
    HomeSalesReceipts,
    CustomerAttraction,
    HomeCostPayment,
    HomeLowStock,
    HomeReceiptPayment,
    HomeProductSales,
    HomeCustomerDistribution,
    HomeBestSellingProducts,
    HomeBankCash,
  },
  props: {
    value: {
      type: String,
      default: "homeTabDashboard",
    },
  },
  data() {
    return {
      period: (() => {
        const allowed = [
          "yesterday",
          "today",
          "lastWeek",
          "week",
          "7days",
          "14days",
          "lastMonth",
          "month",
          "30days",
          "year",
        ];
        const saved = sessionStorage.getItem("homeDashPeriod");
        return allowed.includes(saved) ? saved : "week";
      })(),
      periods: [
        { value: "yesterday", label: "yesterday" },
        { value: "today", label: "today" },
        { value: "lastWeek", label: "one-week-ago" },
        { value: "week", label: "thisWeek" },
        { value: "7days", label: "last7Days" },
        { value: "14days", label: "two-week-ago" },
        { value: "lastMonth", label: "one-month-ago" },
        { value: "month", label: "thisMonth" },
        { value: "30days", label: "30 days" },
        { value: "year", label: "thisYear" },
      ],
      actions: [],
      selectedAction: null,
      pinDialog: false,
      fastAccessSheet: false,
      pinSearch: "",
      pinOpenKeys: [],
      pinningTo: null,
      pinningKind: null,
      yearItems: [],
      sumOfSells: 0,
      totalProfit: 0,
      debtorsTotal: 0,
      creditorsTotal: 0,
      debtorsCount: 0,
      creditorsCount: 0,
      outOfStock: 0,
      popularProducts: 0,
      stagnantProducts: 0,
      sellList: [],
      newCustomersCount: null,
      nowTick: Date.now(),
      clockTimer: null,
      greetStep: 0,
      greetTimer: null,
    };
  },
  computed: {
    isHome() {
      return this.value === "homeTabDashboard" || this.value === "home";
    },
    greetText() {
      return this.greetStep === 0
        ? this.$t("homeWelcomeAbrIron")
        : this.$t("homeHaveAGoodDay");
    },
    greetIcon() {
      return this.greetStep === 0
        ? "mdi-emoticon-happy-outline"
        : "mdi-white-balance-sunny";
    },
    canSeeCustomerReview() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("customerReview")
      );
    },
    canSeeOverview() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("overview")
      );
    },
    periodLabel() {
      const found = this.periods.find((item) => item.value === this.period);
      return found ? this.$t(found.label) : this.$t("thisWeek");
    },
    salesKpiLabel() {
      return `${this.$t("sell")} ${this.periodLabel}`;
    },
    profitKpiLabel() {
      return `${this.$t("profit")} ${this.periodLabel}`;
    },
    dashboardYearName() {
      return this.$store.state.year;
    },
    dateType() {
      return this.period;
    },
    isDesktopHomeLayout() {
      const width =
        (this.$vuetify.breakpoint && this.$vuetify.breakpoint.width) ||
        window.innerWidth;
      return width > 1220;
    },
    sparkPoints() {
      if (!this.sellList.length) return "";
      const values = this.sellList.map((item) => parseFloat(item.sell) || 0);
      const min = Math.min.apply(null, values);
      const max = Math.max.apply(null, values);
      const span = max - min || 1;
      return values
        .map((val, i) => {
          const x = values.length === 1 ? 40 : (i / (values.length - 1)) * 80;
          const y = 22 - ((val - min) / span) * 20;
          return x + "," + y;
        })
        .join(" ");
    },
    sellChange() {
      if (this.sellList.length < 2) return null;
      const first = parseFloat(this.sellList[0].sell) || 0;
      const last = parseFloat(this.sellList[this.sellList.length - 1].sell) || 0;
      if (!first) return null;
      return Math.round(((last - first) / first) * 100);
    },
    clockTime() {
      void this.nowTick;
      return moment().format("HH:mm");
    },
    clockDate() {
      void this.nowTick;
      if (this.$store.state.date_locale === "en,fa") {
        return moment().format("dddd D MMMM YYYY");
      }
      return moment().format("dddd jD jMMMM jYYYY");
    },
    pinMenuGroups() {
      const menus = [];
      if (this.$store.state.topMenu && this.$store.state.topMenu.length) {
        menus.push(...this.$store.state.topMenu);
      }
      if (this.$store.state.menu && this.$store.state.menu.length) {
        menus.push(...this.$store.state.menu);
      }
      const skipKeys = {
        dashboard: true,
        home: true,
        homeTabDashboard: true,
        widget: true,
        widgets: true,
        overview: true,
        grid: true,
      };
      const skipLabels = {};
      Object.keys(skipKeys).forEach((key) => {
        skipLabels[this.$t(key)] = true;
      });
      const skipMenu = (entry) => {
        if (!entry) return true;
        const title = entry.title;
        const to = entry.to;
        if (skipKeys[title] || skipKeys[to]) return true;
        if (skipLabels[title] || skipLabels[this.$t(title)]) return true;
        return false;
      };
      const permissions = this.$store.state.permissions || [];
      const search = (this.pinSearch || "").trim().toLowerCase();
      return menus
        .filter((group) => !skipMenu(group))
        .map((group, index) => {
          const groupLabel = this.$t(group.title);
          const parentMatch =
            !search || String(groupLabel).toLowerCase().indexOf(search) !== -1;
          const items = [];
          const seen = {};
          const pushIfAllowed = (entry) => {
            if (
              !entry ||
              !entry.to ||
              entry.to === "divider" ||
              entry.title === "divider"
            )
              return;
            if (skipMenu(entry)) return;
            if (entry.show === false) return;
            if (!entry.show && permissions.length && permissions.indexOf(entry.to) === -1)
              return;
            if (seen[entry.to]) return;
            if (group.to && entry.to === group.to) return;
            const label = this.$t(entry.title);
            if (
              search &&
              !parentMatch &&
              String(label).toLowerCase().indexOf(search) === -1
            )
              return;
            seen[entry.to] = true;
            items.push({
              title: entry.title,
              to: entry.to,
              icon: entry.icon,
              label,
            });
          };
          (group.children || []).forEach((child) => {
            pushIfAllowed(child);
            if (child && child.add) pushIfAllowed(child.add);
          });
          let parent = null;
          if (group.to && group.to !== "divider" && group.show !== false) {
            if (parentMatch || items.length) {
              parent = {
                title: group.title,
                to: group.to,
                icon: group.icon,
                label: groupLabel,
              };
            }
          }
          return {
            key: (group.to || group.title || index) + "-" + index,
            title: groupLabel,
            icon: group.icon,
            parent,
            items,
          };
        })
        .filter((group) => group.items.length);
    },
  },
  watch: {
    period() {
      if (this.canSeeOverview) this.loadSell();
    },
    canSeeOverview(value) {
      if (value) this.loadOverviewData();
    },
    "$store.state.shortcutItems": {
      deep: true,
      handler() {
        this.loadShortcuts();
      },
    },
    pinDialog(open) {
      if (!open) {
        this.pinSearch = "";
        this.pinOpenKeys = [];
      }
    },
  },
  mounted() {
    this.loadOverviewData();
    this.loadYears();
    this.loadNewCustomers();
    this.loadShortcuts();
    this.startGreetCycle();
    this.clockTimer = setInterval(() => {
      this.nowTick = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.stopGreetCycle();
  },
  methods: {
    isLongKpi(value) {
      return String(this.$numberFormat(value) || "").length > 16;
    },
    go(name, markSelected) {
      if (markSelected) this.selectedAction = name;
      this.$router.push({ name: name }).catch(() => {});
    },
    isActiveChip(item) {
      return this.selectedAction === item.to || this.$route.name === item.to;
    },
    isPinGroupOpen(group) {
      if ((this.pinSearch || "").trim()) return true;
      return this.pinOpenKeys.indexOf(group.key) !== -1;
    },
    togglePinGroup(group) {
      if (!group.items.length) return;
      const index = this.pinOpenKeys.indexOf(group.key);
      if (index === -1) this.pinOpenKeys.push(group.key);
      else this.pinOpenKeys.splice(index, 1);
    },
    isPinned(to) {
      if (this.pinningTo === to) return this.pinningKind === "pin";
      return this.actions.some((item) => item.to === to);
    },
    isSaving(to) {
      return this.pinningTo === to;
    },
    togglePin(item) {
      if (this.pinningTo) return;
      const pinned = this.actions.find((action) => action.to === item.to);
      if (pinned) this.unpin(pinned);
      else this.pin(item);
    },
    pin(item) {
      if (this.pinningTo) return;
      this.pinningTo = item.to;
      this.pinningKind = "pin";
      const formData = new FormData();
      formData.append("title", this.$t(item.title));
      formData.append("to", item.to);
      formData.append("icon", item.icon || "");
      formData.append("color", this.randomColor());
      this.$axios
        .post(this.$endpoint + "shortcut", formData)
        .then(() => {
          this.$store.commit("SET_SHORTCUT_ITEMS", item);
          return this.loadShortcuts();
        })
        .catch(() => {})
        .finally(() => {
          this.pinningTo = null;
          this.pinningKind = null;
        });
    },
    unpin(item) {
      if (!item || !item.id || this.pinningTo) return;
      this.pinningTo = item.to;
      this.pinningKind = "unpin";
      this.$axios
        .delete(this.$endpoint + "shortcut/" + item.id)
        .then(() => this.loadShortcuts())
        .catch(() => {})
        .finally(() => {
          this.pinningTo = null;
          this.pinningKind = null;
        });
    },
    loadShortcuts() {
      return this.$axios
        .get(this.$endpoint + "shortcut")
        .then((res) => {
          this.actions = res.data || [];
        })
        .catch(() => {
          this.actions = [];
        });
    },
    stopGreetCycle() {
      if (this.greetTimer) {
        clearInterval(this.greetTimer);
        this.greetTimer = null;
      }
    },
    startGreetCycle() {
      this.stopGreetCycle();
      this.greetStep = 0;
      this.greetTimer = setInterval(() => {
        this.greetStep = this.greetStep === 0 ? 1 : 0;
      }, 3000);
    },
    toggleReview() {
      this.$emit(
        "input",
        this.value === "customerReview" ? "homeTabDashboard" : "customerReview"
      );
    },
    fromFastSheetGo(item) {
      this.fastAccessSheet = false;
      this.go(item.to, true);
    },
    fromFastSheetMore() {
      this.fastAccessSheet = false;
      this.pinDialog = true;
    },
    setPeriod(value) {
      this.period = value;
      sessionStorage.setItem("homeDashPeriod", value);
    },
    loadNewCustomers() {
      this.$axios
        .get(this.$endpoint + "widgets", {
          params: { type: "newCustomer" },
        })
        .then((res) => {
          this.newCustomersCount = res.data;
        })
        .catch(() => {
          this.newCustomersCount = 0;
        });
    },
    loadYears() {
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: { select: "year", search: null, page: 1 },
        })
        .then((res) => {
          this.yearItems = res.data.data || [];
        })
        .catch(() => {});
    },
    selectYear(year) {
      if (
        this.$store.state.financialYear &&
        this.$store.state.financialYear.id === year.id
      )
        return;
      sessionStorage.setItem("homeDashPeriod", this.period);
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: { select: "changeYear", year: year },
        })
        .then((res) => {
          const data = res.data;
          const storage = JSON.parse(localStorage.getItem("token"));
          storage.init.startDate = data.start_date;
          storage.init.endDate = data.end_date;
          storage.init.financialYear = data;
          storage.init.year = data.name;
          localStorage.setItem("token", JSON.stringify(storage));
          this.$store.commit("SET_YEAR", data.name);
          this.$store.commit("SET_FINANCIALYEAR", data);
          this.$store.commit("SET_START_DATE", data.start_date);
          this.$store.commit("SET_END_DATE", data.end_date);
          location.reload();
        })
        .catch(() => {});
    },
    extractSellList(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.items)) return data.items;
      return [];
    },
    loadOverviewData() {
      if (!this.canSeeOverview) return;
      this.loadSell();
      this.loadProfit();
      this.loadBalance();
      this.loadStock();
    },
    loadSell() {
      this.$axios
        .get(this.$endpoint + "widgets", {
          params: { type: "getSellChart", dateType: this.dateType },
        })
        .then((res) => {
          this.sellList = this.extractSellList(res.data);
          this.sumOfSells = this.sellList.reduce(
            (sum, item) => sum + (parseFloat(item.sell) || 0),
            0
          );
        })
        .catch(() => {
          this.sellList = [];
        });
    },
    loadProfit() {
      this.$axios
        .get(this.$endpoint + "widgets", {
          params: { type: "profit" },
        })
        .then((res) => {
          this.totalProfit = res.data;
        })
        .catch(() => {});
    },
    loadBalance() {
      this.$axios
        .get(this.$endpoint + "customerreport", {
          params: { select: "customersBalanceStatus" },
        })
        .then((res) => {
          const data = res.data || {};
          this.debtorsCount = data.debtorsCount || 0;
          this.creditorsCount = data.creditorsCount || 0;
          this.debtorsTotal = data.debtorsTotal || 0;
          this.creditorsTotal = data.creditorsTotal || 0;
        })
        .catch(() => {});
    },
    loadStock() {
      this.$axios
        .get(this.$endpoint + "productsreport", {
          params: { report: "overview", dateType: "year", threshold: 0.1 },
        })
        .then((res) => {
          const data = this.pickPayload(res.data);
          this.outOfStock = this.pickCount(data, ["orderPointCount"]);
          this.popularProducts = this.pickCount(data, ["sellProductsCount"]);
          this.stagnantProducts = this.pickCount(data, ["productCountWithoutSell"]);
        })
        .catch(() => {});
    },
    pickPayload(data) {
      if (
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        data.data &&
        typeof data.data === "object" &&
        !Array.isArray(data.data)
      ) {
        return data.data;
      }
      return data;
    },
    pickCount(data, keys) {
      if (typeof data === "number") return data;
      if (data == null) return 0;
      for (let i = 0; i < keys.length; i++) {
        if (data[keys[i]] != null && data[keys[i]] !== "")
          return Number(data[keys[i]]) || 0;
      }
      return Number(data) || 0;
    },
  },
};
</script>

<style scoped>
.home-top {
  width: 100%;
  padding: 4px 4px 12px;
}
.home-top__intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}
.home-top__intro--review {
  justify-content: flex-end;
}
.home-top__review-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  max-width: 52%;
}
/* Soft greeting — no card, sits like intro text */
.home-top__greet {
  width: fit-content;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
}
.home-top__greet-inner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.home-top__greet-icon {
  color: #0066ff !important;
  flex-shrink: 0;
}
.home-top__greet-icon--sun {
  color: #f59e0b !important;
}
.home-top__greet-text {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.4;
  white-space: nowrap;
}
.home-top-greet-fade-enter-active,
.home-top-greet-fade-leave-active {
  transition: opacity 0.4s ease;
}
.home-top-greet-fade-enter,
.home-top-greet-fade-leave-to {
  opacity: 0;
}
.home-top__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.home-top__hello {
  font-size: 16px;
  color: #111;
  font-weight: 600;
}
.home-top__sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.home-top__period-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 0 1px;
  border: none;
  border-bottom: 1px solid #0066ff;
  background: transparent;
  border-radius: 0;
  color: #0066ff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
}
.home-top__period-link-icon {
  color: #0066ff !important;
}
.home-top__period {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  color: #111;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.home-top__period--active {
  background: #0066ff;
  border-color: #0066ff;
  color: #fff;
}
.home-top__year {
  min-width: 120px;
}
.home-top__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.home-top__actions-mobile {
  display: none;
}
.home-top__actions-label {
  font-size: 12px;
  color: #6b7280;
  margin-left: 4px;
}
.home-top__fast-sheet {
  border-radius: 16px 16px 0 0 !important;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.home-top__fast-sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: #cfd8dc;
  margin: 10px auto 4px;
  flex-shrink: 0;
}
.home-top__fast-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 10px;
  border-bottom: 1px solid #eef2f6;
  flex-shrink: 0;
}
.home-top__fast-sheet-title {
  font-size: 16px;
  font-weight: 700;
  color: #1c2a3a;
}
.home-top__fast-sheet-spacer,
.home-top__fast-sheet-close {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.home-top__fast-sheet-close {
  border: none;
  background: transparent;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
}
.home-top__fast-sheet-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 12px 20px;
}
.home-top__fast-sheet-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin: 0 0 8px;
  padding: 10px 12px;
  border: 1px solid #e8eef3;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.home-top__fast-sheet-item:active {
  background: #f7f9fc;
}
.home-top__fast-sheet-item--active {
  border-color: #5b7c99;
  background: rgba(91, 124, 153, 0.08);
}
.home-top__fast-sheet-item-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
}
.home-top__fast-sheet-item-meta {
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  flex-shrink: 0;
}
.home-top__fast-sheet-more {
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  min-height: 44px;
  margin: 0 0 8px;
  border: none;
  border-radius: 12px;
  background: #1a3a5c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 8px -6px rgba(28, 42, 58, 0.18);
}
.home-top__chip-wrap {
  position: relative;
  display: inline-flex;
}
.home-top__chip {
  height: 28px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  color: #111;
  cursor: pointer;
}
.home-top__chip--main {
  border-color: #0066ff;
  color: #0066ff;
  background: #e8f1ff;
}
/* Permanent default: new customers — no unpin */
.home-top__chip--customers {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 4px;
  color: #111;
  font-weight: 600;
  flex-shrink: 0;
}
.home-top__chip--customers-active {
  border-color: #0066ff;
  color: #0066ff;
  background: #e8f1ff;
}
.home-top__chip-pin {
  color: #0066ff !important;
  flex-shrink: 0;
  transform: rotate(45deg);
}
.home-top__chip--customers-active .home-top__chip-pin {
  color: #0066ff !important;
}
.home-top__chip-sep {
  opacity: 0.7;
  font-weight: 700;
  color: #9ca3af;
}
.home-top__chip-count {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #0066ff;
}
.home-top__chip-unit {
  font-weight: 500;
  color: #6b7280;
}
.home-top__chip--customers-active .home-top__chip-count {
  color: #0066ff;
}
.home-top__chip--more {
  border-style: dashed;
  color: #6b7280;
}
.home-top__chip-unpin {
  display: none;
  position: absolute;
  top: -7px;
  inset-inline-start: -7px;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 1px solid #e7000b;
  border-radius: 50%;
  background: #e7000b;
  color: #fff;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.home-top__chip-wrap:hover .home-top__chip-unpin {
  display: inline-flex;
}
.home-top__chip-unpin .v-icon {
  color: #fff !important;
}
.home-top__pin-dialog {
  border-radius: 4px !important;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
}
.home-top__pin-top {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.04);
  z-index: 1;
}
.home-top__pin-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111;
}
.home-top__pin-close {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.home-top__pin-close .v-icon {
  color: #6b7280 !important;
}
.home-top__pin-search {
  padding: 0 16px 23px;
  margin-bottom: 0;
}
.home-top__pin-progress {
  height: 2px;
  overflow: hidden;
  background: #e5e7eb;
}
.home-top__pin-progress.is-on span {
  display: block;
  height: 100%;
  width: 34%;
  background: #0066ff;
  animation: home-top-pin-slide 0.9s ease-in-out infinite;
}
@keyframes home-top-pin-slide {
  0% {
    transform: translateX(220%);
  }
  100% {
    transform: translateX(-280%);
  }
}
.home-top__pin-body {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 60vh;
  overflow-y: auto;
  padding: 16px 8px 16px !important;
  background: #f8fafc;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}
.home-top__pin-body::-webkit-scrollbar {
  width: 4px;
}
.home-top__pin-body::-webkit-scrollbar-track {
  background: transparent;
}
.home-top__pin-body::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}
.home-top__pin-empty {
  padding: 24px 8px;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
}
.home-top__pin-group + .home-top__pin-group {
  margin-top: 6px;
}
.home-top__pin-drawer {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #fff;
}
.home-top__pin-drawer:hover {
  background: #f3f4f6;
}
.home-top__pin-group.is-open .home-top__pin-drawer {
  background: #f8fafc;
}
.home-top__pin-drawer-toggle {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-height: 40px;
  padding: 6px 8px;
  border: none;
  background: transparent;
  color: #111;
  font-size: 14px;
  font-weight: 700;
  text-align: start;
  cursor: pointer;
}
.home-top__pin-drawer-toggle .home-top__pin-parent-label .v-icon {
  color: #0066ff !important;
}
.home-top__pin-drawer-end {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-inline-start: auto;
}
.home-top__pin-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  min-width: 20px;
  height: 20px;
  padding: 0;
  margin: 0 4px 0 0;
  border: none;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
}
.home-top__pin-chevron:hover,
.home-top__pin-chevron:focus,
.home-top__pin-chevron:active {
  background: transparent;
  box-shadow: none;
  outline: none;
}
.home-top__pin-chevron .v-icon {
  color: #9ca3af !important;
}
.home-top__pin-pinbtn {
  width: 36px;
  min-width: 36px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.home-top__pin-pinbtn .v-icon {
  color: #9ca3af !important;
}
.home-top__pin-pinbtn.is-pinned .v-icon {
  color: #0066ff !important;
}
.home-top__pin-pinbtn.is-saving {
  cursor: progress;
}
.home-top__pin-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: #111;
}
.home-top__pin-group-title .v-icon {
  color: #0066ff !important;
}
.home-top__pin-parent-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.home-top__pin-children {
  margin-inline-start: 10px;
  padding: 2px 0 8px;
  padding-inline-start: 10px;
  border-inline-start: 2px solid #e8f1ff;
}
.home-top__pin-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 36px;
  padding: 6px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 400;
  text-align: start;
  cursor: pointer;
}
.home-top__pin-item--parent {
  min-height: 40px;
  margin-bottom: 2px;
  font-size: 14px;
  font-weight: 700;
  color: #111;
}
.home-top__pin-item--child {
  color: #4b5563;
}
.home-top__pin-child-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.home-top__pin-child-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  flex-shrink: 0;
  margin-inline-start: auto;
}
.home-top__pin-item:hover {
  background: #f3f4f6;
}
.home-top__pin-item.is-pinned {
  background: #f0f6ff;
  color: #0066ff;
}
.home-top__pin-item.is-saving {
  cursor: progress;
}
.home-top__pin-item .v-icon {
  color: #9ca3af !important;
}
.home-top__pin-item.is-pinned .v-icon {
  color: #0066ff !important;
}
.home-top__metrics {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.home-top__metrics--limited {
  justify-content: flex-end;
  grid-template-columns: minmax(260px, 380px);
}
.home-top__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.home-top__kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}
.home-top__side {
  flex: 0 0 24%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.home-top__kpi {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px 14px;
  height: 108px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  overflow: hidden;
}
.home-top__kpi-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-shrink: 0;
  min-width: 0;
}
.home-top__kpi-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.home-top__kpi-icon--sell {
  background: #e8f1ff;
}
.home-top__kpi-icon--sell .v-icon {
  color: #0066ff !important;
}
.home-top__kpi-icon--profit {
  background: #fff8e1;
}
.home-top__kpi-icon--profit .v-icon {
  color: #eab308 !important;
}
.home-top__kpi-icon--debt {
  background: #e8f8ee;
}
.home-top__kpi-icon--debt .v-icon {
  color: #00a63e !important;
}
.home-top__kpi-icon--credit {
  background: #fff4e5;
}
.home-top__kpi-icon--credit .v-icon {
  color: #f97316 !important;
}
.home-top__kpi-icon--stock {
  background: #fde8ee;
}
.home-top__kpi-icon--stock .v-icon {
  color: #e7000b !important;
}
.home-top__kpi-label {
  font-size: 12px;
  color: #6b7280;
  min-width: 0;
}
.home-top__kpi-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #111;
  line-height: 1.3;
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 0;
}
.home-top__kpi-value span:last-child {
  flex-shrink: 0;
}
.home-top__kpi-value.is-long {
  font-size: 12px;
}
.home-top__spark {
  width: 80px;
  height: 24px;
  margin-top: 6px;
  display: block;
  flex-shrink: 0;
}
.home-top__spark-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
}
.home-top__spark-row .home-top__spark {
  margin-top: 0;
}
.home-top__spark-row .home-top__kpi-foot {
  margin-top: 0;
  padding-top: 0;
}
.home-top__kpi-foot {
  font-size: 11px;
  margin-top: auto;
  padding-top: 4px;
  flex-shrink: 0;
}
.home-top__kpi-foot--green {
  color: #00a63e;
}
.home-top__kpi-foot--blue {
  color: #0066ff;
}
.home-top__kpi-foot--red {
  color: #e7000b;
}
.home-top__ai {
  width: 100%;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  text-align: start;
}
.home-top__ai-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e8f1ff;
  border-radius: 4px;
}
.home-top__ai-text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: #6b7280;
}
.home-top__ai-lead {
  color: #111 !important;
  font-weight: 600;
}
.home-top__ai-action {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #0066ff;
}
.home-top__ai-action .v-icon {
  color: #0066ff !important;
}
.home-top__insights-main {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  align-items: start;
  gap: 12px;
  margin-top: 12px;
}
.home-top__chart-col,
.home-top__debtor-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.home-top__lower-insights {
  display: grid;
  grid-template-columns: 2.4fr 1fr;
  grid-template-areas:
    "fg dc"
    "cost low";
  align-items: start;
  gap: 12px;
  margin-top: 12px;
}
.home-top__cell-fg {
  grid-area: fg;
  min-width: 0;
}
.home-top__cell-dc {
  grid-area: dc;
  min-width: 0;
}
.home-top__cell-cost {
  grid-area: cost;
  min-width: 0;
}
.home-top__cell-low {
  grid-area: low;
  min-width: 0;
}
/* When dc/low cards move out of this grid, do not keep empty columns */
.home-top__lower-insights--compact {
  grid-template-columns: 1fr;
  grid-template-areas:
    "fg"
    "cost";
}
.home-top__split-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: 12px;
  margin-top: 12px;
  width: 100%;
}
.home-top__split-row > * {
  height: 360px;
}
.home-top__split-bottom {
  display: grid;
  grid-template-columns: minmax(280px, 340px) 240px minmax(0, 1fr);
  grid-template-areas: "bsp cd sr";
  align-items: stretch;
  gap: 12px;
  margin-top: 12px;
  width: 100%;
}
.home-top__split-bsp {
  grid-area: bsp;
  min-width: 0;
}
.home-top__split-cd {
  grid-area: cd;
  min-width: 0;
}
.home-top__split-dc {
  grid-area: dc;
  min-width: 0;
}
.home-top__split-sr {
  grid-area: sr;
  min-width: 0;
}
.home-top__split-bottom > * {
  height: 360px;
  min-width: 0;
}
/* Tablet + mobile KPIs: 2 on top (sell/profit), 3 on bottom — desktop unchanged */
@media (max-width: 1220px) {
  .home-top__kpis {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
  .home-top__kpis > .home-top__kpi:nth-child(1),
  .home-top__kpis > .home-top__kpi:nth-child(2) {
    grid-column: span 3;
  }
  .home-top__kpis > .home-top__kpi:nth-child(n + 3) {
    grid-column: span 2;
  }

  /* Insights: trend, checks, debtors, attraction — full width on tablet/mobile */
  .home-top__insights-primary {
    grid-template-columns: 1fr;
  }
  .home-top__insights-primary .home-top__chart-col,
  .home-top__insights-primary .home-top__debtor-col {
    display: contents;
  }
  .home-top__insights-primary ::v-deep .sales-trend,
  .home-top__insights-primary ::v-deep .checks-card,
  .home-top__insights-primary ::v-deep .debtors-card,
  .home-top__insights-primary ::v-deep .attract-card {
    grid-column: 1 / -1;
    width: 100%;
  }
  .home-top__lower-insights {
    grid-template-columns: 1fr;
    grid-template-areas:
      "fg"
      "cost";
  }
  .home-top__side ::v-deep .low-stock {
    width: 100%;
    max-width: 100%;
    height: 412px !important;
  }
  .home-top__split-bottom {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas:
      "bsp cd dc"
      "sr sr sr";
  }
  .home-top__split-bottom > * {
    height: 330px;
    min-height: 0;
  }
  .home-top__split-bottom ::v-deep .bsp-card,
  .home-top__split-bottom ::v-deep .cd-card,
  .home-top__split-bottom ::v-deep .dc-card {
    height: 330px !important;
  }
  .home-top__split-sr {
    height: auto;
    min-height: 260px;
  }
  .home-top__split-bottom ::v-deep .sr-card {
    height: auto !important;
    min-height: 260px;
  }
}

/* Tablet */
@media (max-width: 1220px) and (min-width: 768px) {
  .home-top__intro {
    flex-wrap: nowrap;
    align-items: center;
  }
  .home-top__review-wrap {
    width: auto;
    max-width: 48%;
    flex: 0 0 auto;
  }
  .home-top__actions {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
  }
  .home-top__actions-label,
  .home-top__chip-wrap,
  .home-top__chip,
  .home-top__chip--customers {
    flex-shrink: 0;
  }
  .home-top__metrics {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .home-top__metrics--limited {
    justify-content: stretch;
  }
  .home-top__main,
  .home-top__side {
    flex: none;
    width: 100%;
    max-width: none;
  }
  .home-top__split-row {
    grid-template-columns: 1fr 1fr;
  }
  .home-top__split-row > * {
    height: auto;
    min-height: 300px;
  }
}

/* Mobile layout (visual grid); shell/appbar still uses isMobile < 800 */
@media (max-width: 767px) {
  .home-top {
    padding: 0 0 8px;
  }
  .home-top__intro {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    margin-bottom: 4px;
  }
  .home-top__review-wrap {
    display: none;
  }
  .home-top__greet {
    display: none;
  }
  .home-top__greet-text {
    white-space: normal;
  }
  .home-top__sub {
    flex-wrap: wrap;
    row-gap: 4px;
  }
  .home-top__toolbar {
    margin-bottom: 10px;
  }
  .home-top__actions--desktop {
    display: none !important;
  }
  .home-top__actions-mobile {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    width: 100%;
    padding-top: 5px;
  }
  .home-top__fast-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex: 0 0 auto;
    width: auto;
    max-width: none;
    height: 40px;
    margin-top: 0;
    padding: 0 12px;
    border: 1.5px solid #5b7c99;
    border-radius: 10px;
    background: rgba(91, 124, 153, 0.08);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .home-top__fast-btn:active {
    background: rgba(91, 124, 153, 0.16);
  }
  .home-top__fast-btn-text {
    flex: 0 0 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    color: #5b7c99;
    text-align: center;
    white-space: nowrap;
  }
  .home-top__customers-mini {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    margin-top: 0;
    margin-left: 10px;
    padding: 0;
    border: 1.5px solid #5b7c99;
    border-radius: 10px;
    background: rgba(91, 124, 153, 0.08);
    cursor: pointer;
    overflow: visible;
    -webkit-tap-highlight-color: transparent;
  }
  .home-top__customers-mini:active {
    background: rgba(91, 124, 153, 0.16);
  }
  .home-top__customers-mini--active {
    background: rgba(91, 124, 153, 0.18);
  }
  .home-top__customers-mini-badge {
    position: absolute;
    top: -5px;
    left: -11px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    padding: 0 6px;
    border-radius: 4px;
    background: #1a3a5c;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    white-space: nowrap;
    direction: rtl;
    box-sizing: border-box;
    transform: rotate(-28deg);
    transform-origin: center center;
    box-shadow: 0 1px 3px rgba(26, 58, 92, 0.35);
    pointer-events: none;
  }
  .home-top__metrics {
    flex-direction: column;
    align-items: stretch;
  }
  .home-top__main,
  .home-top__side {
    flex: none;
    width: 100%;
    max-width: none;
  }
  .home-top__kpis {
    gap: 8px;
  }
  .home-top__kpi {
    height: auto;
    min-height: 96px;
    padding: 10px 12px;
  }
  .home-top__kpi-value {
    white-space: normal;
    font-size: 12px;
    flex-wrap: wrap;
  }
  .home-top__kpi-value.is-long {
    font-size: 11px;
  }
  .home-top__ai {
    flex-wrap: wrap;
  }
  .home-top__ai-action {
    width: 100%;
    justify-content: flex-start;
    margin-top: 2px;
  }
  .home-top__insights-primary,
  .home-top__lower-insights,
  .home-top__split-row,
  .home-top__split-bottom {
    margin-top: 10px;
    gap: 10px;
  }
  /* Debtors + attraction: full width (col-12) on mobile */
  .home-top__insights-primary {
    grid-template-columns: 1fr;
  }
  .home-top__insights-primary ::v-deep .debtors-card,
  .home-top__insights-primary ::v-deep .attract-card {
    grid-column: 1 / -1;
    width: 100%;
  }
  .home-top__split-row {
    grid-template-columns: 1fr;
  }
  .home-top__split-row > * {
    height: auto;
    min-height: 260px;
  }
  .home-top ::v-deep .checks-card,
  .home-top ::v-deep .bank-cash,
  .home-top ::v-deep .fg-card,
  .home-top ::v-deep .sales-trend,
  .home-top ::v-deep .sbc-card,
  .home-top ::v-deep .channels-card,
  .home-top ::v-deep .rp-card,
  .home-top ::v-deep .ps-card {
    height: auto !important;
    min-height: 240px;
  }
  .home-top ::v-deep .cost-pay {
    height: 323px !important;
  }
  /* bsp + cd + dc: full width (col-12) on mobile */
  .home-top__split-bottom {
    grid-template-columns: 1fr;
    grid-template-areas:
      "bsp"
      "cd"
      "dc"
      "sr";
  }
  .home-top__split-bottom ::v-deep .bsp-card,
  .home-top__split-bottom ::v-deep .cd-card,
  .home-top__split-bottom ::v-deep .dc-card {
    height: auto !important;
    min-height: 280px;
    width: 100%;
  }
}
</style>
<style>
.home-top__pin-overlay {
  overflow: hidden !important;
}
.home-top__pin-overlay::-webkit-scrollbar {
  width: 4px;
}
.home-top__pin-overlay::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}
</style>
